import React from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Typography } from '../../theme';
import { useI18n } from '../../i18n';
import { useTheme } from '../../theme/useTheme';
import type { PartnerReviewStats } from './reviewsApi';
import { formatAverage, reviewPalette, StarRow } from './StarRating';

// Compact "★★★★★ 4.6 · 24 reviews" line under a partner's address; tapping it
// opens the Reviews tab.
export default function RatingSummary({ stats, loaded, canReview, onPress }: {
  stats: PartnerReviewStats;
  loaded: boolean;
  /** False for the partner's own accounts: no "Be the first". */
  canReview: boolean;
  onPress: () => void;
}) {
  const { t, tp, language } = useI18n();
  const { theme } = useTheme();
  const palette = reviewPalette(theme);
  // Hold the line's height while the aggregate loads so nothing jumps.
  if (!loaded) return <View style={styles.placeholder} testID="rating-summary-loading" />;

  const rounded = Math.round(stats.average * 10) / 10;
  const average = formatAverage(stats.average, language);
  const label = stats.count
    ? t('partnerReviews.summaryA11y').replace('{avg}', average).replace('{count}', String(stats.count))
    : t('partnerReviews.noReviewsYet');
  return (
    <TouchableOpacity
      onPress={onPress}
      style={styles.row}
      activeOpacity={0.75}
      accessibilityRole="button"
      accessibilityLabel={label}
      hitSlop={{ top: 6, bottom: 6 }}
      testID="rating-summary"
    >
      <StarRow value={stats.count ? rounded : 0} size={14} />
      {stats.count ? (
        <>
          <Text style={[styles.average, { color: palette.text }]}>{average}</Text>
          <Text style={[styles.meta, { color: palette.muted }]}>· {tp('partnerReviews.reviewCount', stats.count)}</Text>
        </>
      ) : (
        <Text style={[styles.meta, { color: palette.muted }]} numberOfLines={1}>
          {t('partnerReviews.noReviewsYet')}
          {canReview ? <Text style={[styles.cta, { color: palette.accent }]}>{` · ${t('partnerReviews.beFirst')}`}</Text> : null}
        </Text>
      )}
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  row: { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 8, alignSelf: 'flex-start', minHeight: 20 },
  placeholder: { height: 20, marginTop: 8 },
  average: { fontSize: 13, fontWeight: Typography.extrabold },
  meta: { fontSize: 13, fontWeight: Typography.semibold, flexShrink: 1 },
  cta: { fontWeight: Typography.extrabold },
});
